import { IonButton } from '@ionic/react';
import { usePlot } from '@lidea/shared/data-access/plots';
import { PrevNextButtons } from '@lidea/shared/ui/core';
import {
  MeasureDynamicFields,
  MeasureStaticFields,
} from '@lidea/shared/util/types';
import { Grid, Loader } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, usePrevious } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import classNames from 'classnames';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import MeasuresInputsList from './../measures-inputs-list/measures-inputs-list';
import DynamicFields from './dynamic-fields/dynamic-fields';
import styles from './measure-form.module.css';
import OfflineAlert from './offline-alert/offline-alert';
import { PictureInput } from './picture-input';

export type MeasureFields = {
  static?: MeasureStaticFields;
  dynamic?: MeasureDynamicFields;
};

export type MeasureFormValues = {
  [key: string]: number | string | null;
};

/* eslint-disable-next-line */
export interface MeasureFormProps {
  defaultValues?: MeasureFormValues;
  readonly?: boolean;
  plotId: string;
  analysisId?: string;
  measureId: number;
  measureZone: number;
  title?: string;
  fields: MeasureFields;
  coordinates?: string;
  pending?: boolean;
  submitExpand?: 'block' | 'full';
  formLayout?: 'fill' | 'scrollParent';
  /** Lets IonContent handle scrolling instead of an inner scroll container (mobile). */
  externalScroll?: boolean;
  /** Mobile modals: Ionic buttons (lidea-btn styles are desktop-only). */
  useIonicActions?: boolean;
  /** When set, actions are rendered in the modal header instead of the form footer. */
  hideActions?: boolean;
  /** Scroll focused inputs into view (mobile keyboard). */
  scrollOnFocus?: boolean;
  formId?: string;
  onCancel?: () => void;
  onBack?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onSubmit: (event: MeasureFormValues) => void;
  i18n: {
    submit: string;
    cancel?: string;
    back?: string;
  };
}

export function MeasureForm({
  readonly = false,
  defaultValues = {},
  ...props
}: MeasureFormProps) {
  const history = useHistory();
  const plot = usePlot(Number(props.plotId));
  const isScrollParent = props.formLayout === 'scrollParent';
  const useExternalScroll = isScrollParent && props.externalScroll;
  const scrollOnFocus = props.scrollOnFocus ?? useExternalScroll;
  const speciesGroup = plot.data?.properties.species_group.name_fr;
  const { dynamic, static: staticFields } = props.fields;
  const filteredStaticFields =
    staticFields?.filter((field) => field.name !== 'FEC') || [];
  const { t, i18n } = useTranslation(['analysis', 'plot'], {
    useSuspense: false,
  });

  const [offlineDialogOpened, offlineDialogHandlers] = useDisclosure();

  // @ts-expect-error - ignore
  const validateValue = (value, label, min, max) => {
    const parsedValue = parseFloat(value);
    if (parsedValue < min || parsedValue > max) {
      const message = `${t(`plot:${label}`)} ${t('plot:between')} ${min} ${t(
        'plot:and'
      )} ${max}`;
      notifications.show({ color: 'red', message });
      return message;
    }
  };

  const validations =
    speciesGroup === 'Tournesol'
      ? {
          // STATIC
          PERTES: (value) => validateValue(value, 'PERTES', 0, 100),
          NBCM: (value) => validateValue(value, 'NBCM', 0, 15),

          // DYNAMIC
          NBG: (value) => validateValue(value, 'NBG', 0, 3000),

          NBGP: (value) => validateValue(value, 'NBGP', 0, 1000),
          NBGP1: (value) => validateValue(value, 'NBGP1', 0, 1000),
          NBGP2: (value) => validateValue(value, 'NBGP2', 0, 1000),
          NBGP3: (value) => validateValue(value, 'NBGP3', 0, 1000),
          NBGP4: (value) => validateValue(value, 'NBGP4', 0, 1000),
          NBGP5: (value) => validateValue(value, 'NBGP5', 0, 1000),
          NBGP6: (value) => validateValue(value, 'NBGP6', 0, 1000),
          NBGP7: (value) => validateValue(value, 'NBGP7', 0, 1000),
          NBGP8: (value) => validateValue(value, 'NBGP8', 0, 1000),
          NBGP9: (value) => validateValue(value, 'NBGP9', 0, 1000),
          NBGP10: (value) => validateValue(value, 'NBGP10', 0, 1000),

          FEC: (value) => validateValue(value, 'FEC', 0, 100),
          FEC1: (value) => validateValue(value, 'FEC1', 0, 100),
          FEC2: (value) => validateValue(value, 'FEC2', 0, 100),
          FEC3: (value) => validateValue(value, 'FEC3', 0, 100),
          FEC4: (value) => validateValue(value, 'FEC4', 0, 100),
          FEC5: (value) => validateValue(value, 'FEC5', 0, 100),
          FEC6: (value) => validateValue(value, 'FEC6', 0, 100),
          FEC7: (value) => validateValue(value, 'FEC7', 0, 100),
          FEC8: (value) => validateValue(value, 'FEC8', 0, 100),
          FEC9: (value) => validateValue(value, 'FEC9', 0, 100),
          FEC10: (value) => validateValue(value, 'FEC10', 0, 100),
        }
      : {
          // STATIC
          NBEP1: (value) => validateValue(value, 'NBEP1', 1, 50),
          NBEP2: (value) => validateValue(value, 'NBEP2', 0, 50),

          // DYNAMIC
          NBG1: (value) => validateValue(value, 'NBG1', 1, 50),
          NBG2: (value) => validateValue(value, 'NBG2', 0, 50),
        };

  const initialValues = useMemo<MeasureFormValues>(() => {
    const staticFields =
      props.fields.static?.reduce<MeasureFormValues>((acc, field) => {
        acc[field.name] = field.value;
        return acc;
      }, {}) || {};

    const dynamicFields =
      dynamic?.reduce<MeasureFormValues>((acc, field) => {
        field.fields.forEach((f) => {
          acc[f.name] = f.value;
        });

        return acc;
      }, {}) || {};

    return {
      ...staticFields,
      ...dynamicFields,
      ...defaultValues,
    };
  }, [defaultValues, dynamic, props.fields.static]);

  const form = useForm<MeasureFormValues>({
    initialValues,
    validate: validations,
  });

  const prevInitialValues = usePrevious(initialValues);

  const handleSubmit = (values: MeasureFormValues) => {
    const getDirtyValues = () => {
      const dirtyValues = Object.keys(values).reduce((acc, key) => {
        if (form.isDirty(key)) {
          acc[key] = values[key];
        }
        return acc;
      }, {} as MeasureFormValues);

      return dirtyValues;
    };
    const dirtyValues = getDirtyValues();
    props.onSubmit(dirtyValues);
  };

  // Sync server-side changes (e.g. image processing results) without wiping
  // other fields the user typed locally but has not validated yet.
  useEffect(() => {
    if (!prevInitialValues) {
      return;
    }

    const patch = Object.keys(initialValues).reduce<MeasureFormValues>(
      (acc, key) => {
        if (prevInitialValues[key] !== initialValues[key]) {
          acc[key] = initialValues[key];
        }
        return acc;
      },
      {}
    );

    if (Object.keys(patch).length === 0) {
      return;
    }

    form.setValues({
      ...form.values,
      ...patch,
    });
  }, [form, initialValues, prevInitialValues]);

  const handleBack = () => {
    if (props.onBack) {
      props.onBack();
    } else {
      history.push(`/plots/${props.plotId}`);
    }
  };

  const header = props.title ? (
    <div className={styles.formHeader}>
      <div className={styles.formHeaderTitle}>
        <h3>{props.title}</h3>
        <PrevNextButtons onNext={props.onNext} onPrev={props.onPrevious} />
      </div>
      {props.coordinates ? (
        <div className={styles.coordinates}>{props.coordinates}</div>
      ) : null}
    </div>
  ) : null;

  const formBody = (
    <div className={styles.formBody}>
          {speciesGroup === 'Mais' ? (
            filteredStaticFields?.length ? (
              <div className={styles['static-fields']}>
                <MeasuresInputsList
                  analysisId={
                    props.analysisId ? Number(props.analysisId) : undefined
                  }
                  fields={filteredStaticFields}
                  form={form}
                  labelPlacement="stacked"
                  measureId={props.measureId}
                  measureZone={props.measureZone}
                  plotId={props.plotId}
                  readonly={readonly}
                  scrollOnFocus={scrollOnFocus}
                />
              </div>
            ) : null
          ) : staticFields?.length ? (
            <div className={styles['static-fields']}>
              <MeasuresInputsList
                analysisId={
                  props.analysisId ? Number(props.analysisId) : undefined
                }
                fields={staticFields}
                form={form}
                labelPlacement="stacked"
                measureId={props.measureId}
                measureZone={props.measureZone}
                plotId={props.plotId}
                readonly={readonly}
                scrollOnFocus={scrollOnFocus}
              />
            </div>
          ) : null}

          {dynamic?.length ? (
            <Grid className={styles['dynamic-fields']}>
              {dynamic
                ?.filter(
                  (group) =>
                    speciesGroup === 'Tournesol' && group.name === 'NBG'
                )
                .map((group, index) => (
                  <Grid.Col
                    key={index}
                    span={12}
                    style={{ position: 'relative' }}
                  >
                    <h3>{group.label?.fr}</h3>
                    {group.image_processing !== null && !readonly ? (
                      <PictureInput
                        analysisId={
                          props.analysisId ? Number(props.analysisId) : undefined
                        }
                        fieldId={props.plotId}
                        group={group.name}
                        hasProcessError={group.image_processing_error}
                        imageUrl={group.image_url}
                        isProcessing={group.image_processing === true}
                        measureId={props.measureId}
                        measureZone={props.measureZone}
                      />
                    ) : null}
                  </Grid.Col>
                ))}
            </Grid>
          ) : null}

          {dynamic?.length ? (
            <Grid className={styles['dynamic-fields-col']}>
              {dynamic
                ?.filter(
                  (group) =>
                    !(speciesGroup === 'Tournesol' && group.name === 'NBG')
                )
                .map((group, index) => (
                  <Grid.Col
                    key={index}
                    span={
                      12 /
                      ((dynamic?.length || 1) -
                        (speciesGroup === 'Tournesol' ? 1 : 0))
                    }
                    style={{ position: 'relative' }}
                  >
                    <h3>{group.label?.fr}</h3>

                    {group.image_processing !== null && !readonly ? (
                      <PictureInput
                        analysisId={
                          props.analysisId ? Number(props.analysisId) : undefined
                        }
                        fieldId={props.plotId}
                        group={group.name}
                        hasProcessError={group.image_processing_error}
                        imageUrl={group.image_url}
                        isProcessing={group.image_processing === true}
                        measureId={props.measureId}
                        measureZone={props.measureZone}
                      />
                    ) : null}
                    <DynamicFields
                      fields={group.fields}
                      fieldsLabel={
                        group.fieldsLabel[i18n.language as 'en' | 'fr']
                      }
                      form={form}
                      readonly={readonly}
                      scrollOnFocus={scrollOnFocus}
                    />
                  </Grid.Col>
                ))}
            </Grid>
          ) : null}
    </div>
  );

  const actions = !readonly && !props.hideActions ? (
    <div
      className={classNames(styles.formButtons, {
        [styles.formButtonsIonic]: props.useIonicActions,
      })}
    >
      <input style={{ display: 'none' }} type="submit" />
      {props.useIonicActions ? (
        <>
          {props.onCancel && props.i18n.cancel ? (
            <IonButton
              color="danger"
              disabled={props.pending}
              expand="block"
              type="button"
              onClick={props.onCancel}
            >
              {props.i18n.cancel}
            </IonButton>
          ) : null}
          <IonButton
            color="success"
            disabled={props.pending}
            expand="block"
            type="submit"
          >
            {props.i18n.submit}
            {props.pending ? <Loader color="white" ml="sm" size="xs" /> : null}
          </IonButton>
        </>
      ) : (
        <>
          {props.i18n.back ? (
            <button
              className="lidea-btn lidea-btn--ghost"
              type="button"
              onClick={handleBack}
            >
              {props.i18n.back}
            </button>
          ) : null}
          <button
            className={classNames('lidea-btn', 'lidea-btn--filled', {
              'lidea-btn--block': props.submitExpand === 'block',
            })}
            disabled={props.pending}
            type="submit"
          >
            {props.i18n.submit}
            {props.pending ? <Loader color="white" ml="sm" size="xs" /> : null}
          </button>
        </>
      )}
    </div>
  ) : null;

  const formContent = isScrollParent ? (
    <div
      className={classNames(styles.stack, {
        [styles.stackExternalScroll]: useExternalScroll,
      })}
    >
      <div
        className={classNames(styles.stackFields, {
          [styles.stackFieldsNoFooter]: props.hideActions,
          [styles.stackFieldsExternalScroll]: useExternalScroll,
        })}
      >
        {header}
        {formBody}
      </div>
      {actions ? <div className={styles.stackFooter}>{actions}</div> : null}
    </div>
  ) : (
    <>
      {header}
      {formBody}
      {actions}
    </>
  );

  return (
    <>
      <form
        id={props.formId}
        className={classNames(styles.form, {
          [styles.formScrollParent]: isScrollParent && !useExternalScroll,
          [styles.formExternalScroll]: useExternalScroll,
        })}
        onSubmit={form.onSubmit((values) => handleSubmit(values))}
      >
        {formContent}
      </form>

      <OfflineAlert
        isOpen={offlineDialogOpened}
        onClose={offlineDialogHandlers.close}
      />
    </>
  );
}

export default MeasureForm;
